import { useState, useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { VoiceClient, VoiceClientState, ExtractedItemEvent, VoiceClientConfig } from '../lib/voice-client';
import { api } from '../services/api';
import { getAccessToken } from '../store/auth';

export type SessionState = 'idle' | 'connecting' | 'active' | 'ending' | 'ended' | 'error';

export interface ExtractedItem {
  id: string;
  title: string;
  category: string;
  confidence: number;
}

export interface UseVoiceSessionReturn {
  sessionState: SessionState;
  extractedItems: ExtractedItem[];
  audioLevel: number;
  transcript: string;
  error: string | null;
  sessionId: string | null;
  startSession: (inventoryId: string) => Promise<void>;
  endSession: () => Promise<void>;
}

export function useVoiceSession(): UseVoiceSessionReturn {
  const queryClient = useQueryClient();
  const [sessionState, setSessionState] = useState<SessionState>('idle');
  const [extractedItems, setExtractedItems] = useState<ExtractedItem[]>([]);
  const [audioLevel, setAudioLevel] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const voiceClientRef = useRef<VoiceClient | null>(null);
  const inventoryIdRef = useRef<string | null>(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (voiceClientRef.current) {
        voiceClientRef.current.disconnect();
      }
    };
  }, []);

  const startSession = useCallback(async (inventoryId: string) => {
    if (sessionState !== 'idle' && sessionState !== 'ended' && sessionState !== 'error') {
      return;
    }

    setSessionState('connecting');
    setError(null);
    setExtractedItems([]);
    setTranscript('');
    inventoryIdRef.current = inventoryId;

    try {
      // Create session on our backend
      const response = await api.realtime.createSession(inventoryId);
      setSessionId(response.sessionId);

      // Create voice client with event handlers
      const client = new VoiceClient({
        onStateChange: (state: VoiceClientState) => {
          if (state === 'connected') {
            setSessionState('active');
          } else if (state === 'error') {
            setSessionState('error');
          }
        },
        onItemExtracted: (item: ExtractedItemEvent) => {
          setExtractedItems(prev => [...prev, {
            id: item.itemId,
            title: item.title,
            category: item.category,
            confidence: item.confidence,
          }]);
          // Invalidate queries so UI updates immediately
          queryClient.invalidateQueries({ queryKey: ['items'] });
          queryClient.invalidateQueries({ queryKey: ['inventories'] });
        },
        onTranscript: (text: string, isFinal: boolean) => {
          if (isFinal) {
            setTranscript(prev => prev + ' ' + text);
          }
        },
        onError: (err: Error) => {
          console.error('Voice client error:', err);
          setError(err.message);
        },
        onAudioLevel: (level: number) => {
          setAudioLevel(level);
        },
      });

      voiceClientRef.current = client;

      // Build config with inventoryId and access token
      const config: VoiceClientConfig = {
        ephemeralKey: response.ephemeralKey,
        model: response.config.model,
        voice: response.config.voice,
        instructions: response.config.instructions,
        inventoryId: inventoryId,
        accessToken: getAccessToken() || '',
        tools: response.config.tools,
      };

      // Connect to OpenAI
      await client.connect(config);

    } catch (err) {
      console.error('Failed to start session:', err);
      setError(err instanceof Error ? err.message : 'Failed to start session');
      setSessionState('error');
    }
  }, [sessionState]);

  const endSession = useCallback(async () => {
    if (sessionState !== 'active') {
      return;
    }

    setSessionState('ending');

    try {
      // Disconnect voice client
      if (voiceClientRef.current) {
        await voiceClientRef.current.disconnect();
        voiceClientRef.current = null;
      }

      // End session on backend
      if (sessionId) {
        await api.realtime.endSession(sessionId);
      }

      setSessionState('ended');
    } catch (err) {
      console.error('Failed to end session:', err);
      setError(err instanceof Error ? err.message : 'Failed to end session');
      setSessionState('error');
    }
  }, [sessionState, sessionId]);

  return {
    sessionState,
    extractedItems,
    audioLevel,
    transcript,
    error,
    sessionId,
    startSession,
    endSession,
  };
}
